import React from 'react';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Paper, TableContainer, Pagination, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, Grid } from '@mui/material';
import { Link, Link as RouterLink } from 'react-router-dom';
import { Delete, Edit, Restore } from '@mui/icons-material';
import { toast } from "react-toastify";
import Loader from 'UI/Application/Components/Loaders/Loader';
import DialogLoader from 'UI/Application/Components/Loaders/DialogLoader';
import { getPermissionGroupsList } from 'PermissionGroup/Domain/Services/GetPermissionGroupsListService';
import { deletePermissionGroup } from 'PermissionGroup/Domain/Services/DeletePermissionGroupService';
import { patchPermissionGroup } from 'PermissionGroup/Domain/Services/PatchPermissionGroupService';
import { PRIVATE_ADMIN_FORM_PERMISSIONGROUP } from '../Constants/PathsConstant';
import { useTranslation } from 'react-i18next';

function TablePermissionGroupView() {
  const {t} = useTranslation();
  const [sendingApiRequest, setSendingApiRequest] = React.useState(false);
  const limit = 10;
  const [offset, setOffset] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [lastPage, setLastPage] = React.useState(1);
  const [permissionGroups, setPermissionGroups] = React.useState([]);
  const [permissionGroupsRows, setPermissionGroupsRows] = React.useState([]);
  const [loadPermissionGroups, setLoadPermissionGroups] = React.useState(true);
  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);
  const [openRestoreModal, setOpenRestoreModal] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState(null);

  const [filterSku, setFilterSku] = React.useState('');
  const [filterDescription, setFilterDescription] = React.useState('');
  const [filterDeleted, setFilterDeleted] = React.useState(0);

  // Fetch data on mount or when loadPermissionGroups flag changes
  React.useEffect(() => {
    if (loadPermissionGroups) {
      var data = {
        offset: offset,
        limit: limit
      };
      data = {
        ...data,
        filterSku: filterSku,
        filterDescription: filterDescription,
        filterDeleted: filterDeleted
      };
      getPermissionGroupsList(data).then((response) => {
        setPermissionGroups(response.records);
        setLastPage(Math.ceil(response.total / limit));
        setLoadPermissionGroups(false);
        if (Math.ceil(response.total / limit) < currentPage) {
          setCurrentPage(Math.ceil(response.total / limit));
          if (((Math.ceil(response.total / limit) - 1) * limit) < 0) {
            setOffset(0);
          } else {
            setOffset((Math.ceil(response.total / limit) - 1) * limit);
            setLoadPermissionGroups(true);
          }
        }
      });
    }
  }, [
    loadPermissionGroups,
    offset,
    limit,
    filterSku,
    filterDescription,
    filterDeleted
  ]);

  const handleFilterSkuChange = (event) => {
    setFilterSku(event.target.value);
    setOffset(0);
    setCurrentPage(1);
    setLoadPermissionGroups(true);
  };

  const handleFilterDescriptionChange = (event) => {
    setFilterDescription(event.target.value);
    setOffset(0);
    setCurrentPage(1);
    setLoadPermissionGroups(true);
  };

  const handleFilterDeletedChange = (event) => {
    setFilterDeleted(event.target.value);
    setOffset(0);
    setCurrentPage(1);
    setLoadPermissionGroups(true);
  };

  const handleDelete = (id) => {
    setSelectedId(id);
    setOpenDeleteModal(true);
  };

  const handleRestore = (id) => {
    setSelectedId(id);
    setOpenRestoreModal(true);
  };

  const handleConfirmDelete = () => {
    if (selectedId && !sendingApiRequest) {
      setSendingApiRequest(true);
      deletePermissionGroup(selectedId).then((response) => {
        if (response) {
          setOpenDeleteModal(false);
          setLoadPermissionGroups(true);
          setSelectedId(null);
          toast.success(t('permissiongroup.table.modal.delete.done'));
        }
      }).finally(() => {
        setSendingApiRequest(false);
      });
    }
  };

  const handleConfirmRestore = () => {
    if (selectedId && !sendingApiRequest) {
      setSendingApiRequest(true);
      patchPermissionGroup(selectedId, {
        restore: true
      }).then((response) => {
        if (response) {
          setOpenRestoreModal(false);
          setLoadPermissionGroups(true);
          setSelectedId(null);
          toast.success(t('permissiongroup.table.modal.restore.done'));
        }
      }).finally(() => {
        setSendingApiRequest(false);
      });
    }
  };

  // Update table rows
  React.useEffect(() => {
    const rows = permissionGroups.map((permissionGroup) => (
      <TableRow key={permissionGroup.sku}>
        <TableCell align='center'>{permissionGroup.sku}</TableCell>
        <TableCell align='center'>{permissionGroup.description}</TableCell>
        <TableCell align="center" style={{ display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
          {permissionGroup.deletedAt === null ? (<>
            <Button variant="contained" color="error" onClick={() => handleDelete(permissionGroup.sku)}>
              <Delete />
            </Button>
            <Button variant="contained" color="primary" component={Link} to={(`${PRIVATE_ADMIN_FORM_PERMISSIONGROUP}/${permissionGroup.sku}`)}>
              <Edit />
            </Button>
            </>) : (
            <Button variant="contained" color="success" onClick={() => handleRestore(permissionGroup.sku)}>
              <Restore />
            </Button>
          )}
          </Box>
        </TableCell>
      </TableRow>
    ));
    setPermissionGroupsRows(rows);
  }, [permissionGroups]);

  // Update offset when page changes
  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
    if (((newPage - 1) * limit) < 0) {
      setOffset(0);
    } else {
      setOffset((newPage - 1) * limit);
    }
    setLoadPermissionGroups(true);
  };

  return (
    <>
      <Paper
          sx={{
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          with: "100%"
          }}
      >
          <Box sx={{ p: 2, flex: 1 }}>
              <Box sx={{ marginBottom: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
                      <Typography variant="subtitle1" style={{ marginBottom: 5 }}>
                        {t('permissiongroup.table.filter_by_sku')}
                      </Typography>
                      <TextField
                        variant="outlined"
                        size="small"
                        value={filterSku}
                        onChange={handleFilterSkuChange}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
                      <Typography variant="subtitle1" style={{ marginBottom: 5 }}>
                        {t('permissiongroup.table.filter_by_description')}
                      </Typography>
                      <TextField
                        variant="outlined"
                        size="small"
                        value={filterDescription}
                        onChange={handleFilterDescriptionChange}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
                      <Typography variant="subtitle1" style={{ marginBottom: 5 }}>
                        {t('permissiongroup.table.filter_by_deleted')}
                      </Typography>
                      <Select
                        size="small"
                        value={filterDeleted}
                        onChange={handleFilterDeletedChange}
                      >
                        <MenuItem value={0}>{t('permissiongroup.table.filter_by_deleted_not_deleted')}</MenuItem>
                        <MenuItem value={1}>{t('permissiongroup.table.filter_by_deleted_deleted')}</MenuItem>
                        <MenuItem value={2}>{t('permissiongroup.table.filter_by_deleted_all')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    component={RouterLink}
                    to={(PRIVATE_ADMIN_FORM_PERMISSIONGROUP + '/new')}
                    style={{ height: 40, marginRight: 10 }}
                  >
                    {t('permissiongroup.table.button.create')}
                </Button>
              </Box>
              <Typography variant="h6" sx={{ mb: 2, textAlign: "center"}}>Usuarios</Typography>
              <TableContainer component={Paper}>
              <Table sx={{ minWidth: "100%" }} aria-label="channels table">
                  <TableHead>
                  <TableRow>
                      <TableCell align='center'>{t('permissiongroup.table.sku')}</TableCell>
                      <TableCell align='center'>{t('permissiongroup.table.description')}</TableCell>
                      <TableCell align="center" style={{display: 'table-cell'}}>{t('permissiongroup.table.actions')}</TableCell>
                  </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadPermissionGroups ? (
                      <TableRow>
                        <TableCell colSpan={100} align="center">
                          <Loader />
                        </TableCell>
                      </TableRow>
                    ) : (
                      permissionGroupsRows
                    )}
                  </TableBody>
              </Table>
              </TableContainer>

              {/* Add paginator component */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Pagination count={lastPage} page={currentPage} onChange={handlePageChange} />
              </Box>

              {/* Delete confirmation modal */}
              <Dialog open={openDeleteModal} onClose={() => setOpenDeleteModal(false)}>
                <DialogTitle>{t('permissiongroup.table.modal.delete.title')}</DialogTitle>
                <DialogContent>
                  <Typography variant="body1">{t('permissiongroup.table.modal.delete.phrase')}</Typography>
                </DialogContent>
                <DialogActions>
                  <Button color="secondary" onClick={() => setOpenDeleteModal(false)}>{t('permissiongroup.table.modal.delete.button.cancel')}</Button>
                  <Button variant="contained" color="error" onClick={handleConfirmDelete}>{t('permissiongroup.table.modal.delete.button.delete')}</Button>
                </DialogActions>
              </Dialog>

              {/* Restore confirmation modal */}
              <Dialog open={openRestoreModal} onClose={() => setOpenRestoreModal(false)}>
                <DialogTitle>{t('permissiongroup.table.modal.restore.title')}</DialogTitle>
                <DialogContent>
                  <Typography variant="body1">{t('permissiongroup.table.modal.restore.phrase')}</Typography>
                </DialogContent>
                <DialogActions>
                  <Button color="secondary" onClick={() => setOpenRestoreModal(false)}>{t('permissiongroup.table.modal.restore.button.cancel')}</Button>
                  <Button variant="contained" color="error" onClick={handleConfirmRestore}>{t('permissiongroup.table.modal.restore.button.restore')}</Button>
                </DialogActions>
              </Dialog>
          </Box>
          <DialogLoader open={sendingApiRequest} />
      </Paper>
    </>
  );
}

export default TablePermissionGroupView;
